import { AnimatePresence, motion, useInView, useReducedMotion } from '@/lib/motion';
import {
  Bot,
  CalendarClock,
  CheckCircle2,
  Headphones,
  MessageSquare,
  MoonStar,
  PhoneForwarded,
  RefreshCw,
  Tag,
  User,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { track } from '@/lib/analytics';

import { Waveform } from './waveform';

interface Turn {
  role: 'caller' | 'agent';
  text: string;
}

interface Scenario {
  id: string;
  label: string;
  icon: typeof CalendarClock;
  /** Which channel this conversation simulates — drives the customer-side icon
   *  and the header label. Defaults to voice. */
  channel: 'voice' | 'chat';
  turns: Turn[];
  outcome: { title: string; points: string[] };
}

/**
 * Scripted conversations. Honesty constraint: outcomes only show things the
 * product actually does — capture details, take messages, transfer, answer
 * from the knowledge base. No calendar-write or CRM claims.
 */
const SCENARIOS: Scenario[] = [
  {
    id: 'booking',
    label: 'New appointment',
    icon: CalendarClock,
    channel: 'voice',
    turns: [
      { role: 'caller', text: 'Hi, I’d like to book a check-up sometime next week.' },
      {
        role: 'agent',
        text: 'Happy to help. We do check-ups Monday to Friday, 9 to 5. Does a morning or afternoon suit you better?',
      },
      { role: 'caller', text: 'Tuesday morning if possible.' },
      {
        role: 'agent',
        text: 'Tuesday morning it is. Can I grab your name and best contact number so the team can confirm your time?',
      },
      { role: 'caller', text: 'Sam Carter, (415) 555-0119.' },
      {
        role: 'agent',
        text: 'Thanks Sam — I’ve noted Tuesday morning for a check-up. The team will text you the confirmed time shortly. Anything else?',
      },
    ],
    outcome: {
      title: 'Booking request captured',
      points: [
        'Name + number collected',
        'Preferred time recorded',
        'Summary in your dashboard instantly',
      ],
    },
  },
  {
    id: 'after-hours',
    label: 'After-hours call',
    icon: MoonStar,
    channel: 'voice',
    turns: [
      { role: 'caller', text: 'Hey, are you open? My hot water system just died.' },
      {
        role: 'agent',
        text: 'We’re closed right now — we open at 7am. I can take your details so the first available plumber calls you back, or flag it as urgent.',
      },
      { role: 'caller', text: 'It can wait till morning, just get someone to call me.' },
      {
        role: 'agent',
        text: 'No problem. What’s your name, number, and suburb? I’ll put you at the top of the morning callback list.',
      },
    ],
    outcome: {
      title: 'Lead saved at 11:48 PM',
      points: [
        'Message taken, marked for morning callback',
        'Caller details + issue summary recorded',
        'Nothing lost to voicemail',
      ],
    },
  },
  {
    id: 'transfer',
    label: 'Urgent transfer',
    icon: PhoneForwarded,
    channel: 'voice',
    turns: [
      { role: 'caller', text: 'I need to speak to someone right now — water is leaking through my ceiling.' },
      {
        role: 'agent',
        text: 'That sounds urgent — I’m putting you straight through to our on-call line now. Stay with me one moment.',
      },
    ],
    outcome: {
      title: 'Transferred to on-call mobile',
      points: [
        'Matched your “emergency” transfer rule',
        'Caller connected in seconds',
        'Full transcript kept for the morning',
      ],
    },
  },
  {
    id: 'pricing',
    label: 'Pricing question',
    icon: Tag,
    channel: 'voice',
    turns: [
      { role: 'caller', text: 'How much is a standard cut and blow-dry?' },
      {
        role: 'agent',
        text: 'A standard cut and blow-dry is $85, or $110 with a treatment. With our senior stylists it’s $105. Would you like to book in?',
      },
      { role: 'caller', text: 'Not yet — just checking. Thanks!' },
      {
        role: 'agent',
        text: 'No worries at all. We’re open till 7 on Thursdays if that helps. Have a great day!',
      },
    ],
    outcome: {
      title: 'Answered from your knowledge base',
      points: [
        'Pricing pulled from your uploaded docs',
        'No made-up answers — only your content',
        'Enquiry logged with a summary',
      ],
    },
  },
  {
    id: 'website-chat',
    label: 'Website chat',
    icon: MessageSquare,
    channel: 'chat',
    turns: [
      { role: 'caller', text: 'Hi — do you deliver to postcode 2010?' },
      {
        role: 'agent',
        text: 'We do! Orders to 2010 ship same-day if placed before 2pm, otherwise next business day. Want me to note anything for your order?',
      },
      { role: 'caller', text: 'Is there a delivery fee under $50?' },
      {
        role: 'agent',
        text: 'Yes — a $7 flat fee under $50, free over that. This all comes from our shipping policy, so it’s always up to date.',
      },
    ],
    outcome: {
      title: 'Answered on your website, instantly',
      points: [
        'Same knowledge base as the phone agent',
        'Streamed with a citation to your shipping policy',
        'Conversation captured in your dashboard',
      ],
    },
  },
];

/** ms before each turn appears; agent turns get a typing beat first. */
const TURN_DELAY_MS = 1400;
const DONE_PAUSE_MS = 900;

// SCENARIOS is non-empty by construction; give TS a typed anchor for lookups.
const DEFAULT_SCENARIO = SCENARIOS[0] as Scenario;

export function DemoSection(): JSX.Element {
  const [scenarioId, setScenarioId] = useState(DEFAULT_SCENARIO.id);
  const scenario =
    SCENARIOS.find((s) => s.id === scenarioId) ?? DEFAULT_SCENARIO;

  return (
    <section id="demo" className="container scroll-mt-24 py-14 sm:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <span className="text-xs font-semibold uppercase tracking-widest text-[var(--brand-accent)]">
          Interactive demo
        </span>
        <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
          See it handle a real conversation.
        </h2>
        <p className="mt-4 text-muted-foreground">
          Pick a scenario — on the phone or on your website — and watch how the
          agent responds, captures, and escalates.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">Simulated conversation</p>
      </div>

      {/* Scenario tabs */}
      <div
        role="tablist"
        aria-label="Demo scenarios"
        className="mx-auto mt-10 flex max-w-2xl flex-wrap justify-center gap-2"
      >
        {SCENARIOS.map((s) => {
          const active = s.id === scenarioId;
          return (
            <button
              key={s.id}
              role="tab"
              type="button"
              aria-selected={active}
              onClick={() => setScenarioId(s.id)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                active
                  ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)]/10 text-[var(--brand-accent)]'
                  : 'border-border bg-card text-muted-foreground hover:border-[var(--brand-accent)]/40 hover:text-foreground'
              }`}
            >
              <s.icon className="h-4 w-4" />
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="mx-auto mt-8 max-w-3xl">
        <ScenarioPlayer key={scenario.id} scenario={scenario} />
      </div>
    </section>
  );
}

function ScenarioPlayer({ scenario }: { scenario: Scenario }): JSX.Element {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, { once: true, margin: '-120px' });

  // visible = number of turns shown; turns.length + 1 means "outcome shown".
  const [visible, setVisible] = useState(reduced ? scenario.turns.length + 1 : 0);

  useEffect(() => {
    if (inView) track('demo_played', { scenario: scenario.id });
  }, [inView, scenario.id]);

  useEffect(() => {
    if (!inView || reduced) return;
    if (visible > scenario.turns.length) return;
    const timer = setTimeout(
      () => setVisible((v) => v + 1),
      visible === scenario.turns.length ? DONE_PAUSE_MS : TURN_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [inView, reduced, visible, scenario.turns.length]);

  const done = visible > scenario.turns.length;
  const playing = inView && !done;

  return (
    <div
      ref={ref}
      className="overflow-hidden rounded-2xl border border-border bg-card/60 shadow-2xl shadow-[var(--brand-accent)]/10 backdrop-blur"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-3">
        <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
          Simulated conversation
        </span>
        <div className="flex items-center gap-3">
          {playing && scenario.channel === 'voice' ? (
            <Waveform bars={14} className="h-4 w-16" />
          ) : null}
          <button
            type="button"
            onClick={() => setVisible(0)}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Replay scenario"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Replay
          </button>
        </div>
      </div>

      {/* Transcript */}
      <div className="min-h-[280px] space-y-4 px-6 py-6">
        <AnimatePresence initial={false}>
          {scenario.turns.slice(0, visible).map((turn, i) => (
            <DemoBubble key={i} turn={turn} channel={scenario.channel} />
          ))}
          {playing && scenario.turns[visible]?.role === 'agent' ? (
            <TypingIndicator key="typing" />
          ) : null}
        </AnimatePresence>
      </div>

      {/* Outcome */}
      <AnimatePresence>
        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="border-t border-border bg-emerald-500/5 px-6 py-5"
          >
            <p className="flex items-center gap-2 font-semibold text-emerald-700 dark:text-emerald-400">
              <CheckCircle2 className="h-5 w-5" />
              {scenario.outcome.title}
            </p>
            <ul className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-3">
              {scenario.outcome.points.map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-500" />
                  {p}
                </li>
              ))}
            </ul>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function DemoBubble({
  turn,
  channel,
}: {
  turn: Turn;
  channel: 'voice' | 'chat';
}): JSX.Element {
  const isAgent = turn.role === 'agent';
  const CustomerIcon = channel === 'chat' ? User : Headphones;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex gap-3 ${isAgent ? 'flex-row' : 'flex-row-reverse'}`}
    >
      <div
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
          isAgent
            ? 'bg-[var(--brand-accent)]/15 text-[var(--brand-accent)]'
            : 'bg-muted text-muted-foreground'
        }`}
      >
        {isAgent ? <Bot className="h-4 w-4" /> : <CustomerIcon className="h-4 w-4" />}
      </div>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
          isAgent
            ? 'rounded-tl-sm bg-muted/50 text-foreground'
            : 'rounded-tr-sm bg-[var(--brand-accent)] text-white'
        }`}
      >
        {turn.text}
      </div>
    </motion.div>
  );
}

function TypingIndicator(): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex gap-3"
    >
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--brand-accent)]/15 text-[var(--brand-accent)]">
        <Bot className="h-4 w-4" />
      </div>
      <div className="inline-flex items-center gap-1 rounded-2xl rounded-tl-sm bg-muted/50 px-4 py-3">
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.3s]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.15s]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60" />
      </div>
    </motion.div>
  );
}
